import { Alert } from '@ctw/shared/components/alert';

import { ReactHtmlRenderer } from '@ctw/shared/content/document-viewer/_internal/renderers/react/react-html-renderer';
import { ReactLinkRenderer } from '@ctw/shared/content/document-viewer/_internal/renderers/react/react-link-renderer';
import {
  DOCUMENT_VIEWER_BR_TEST_ID,
  DOCUMENT_VIEWER_CDATA_TEST_ID,
  DOCUMENT_VIEWER_TEXT_TEST_ID,
} from '@ctw/shared/test/ids';
import type { CdaParsedHtmlNode } from '@ctw/shared/utils/cda/types';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import { memo } from 'react';
import { JSX } from 'react/jsx-runtime';
import { ReactHeadingRenderer } from './react-heading-renderer';
import { ReactListRenderer } from './react-list-renderer';
import { ReactTableRenderer } from './react-table-renderer';

import IntrinsicElements = JSX.IntrinsicElements;

import {
  elementNamesIntentionallyOmittedFromRendering,
  filterChildNodesByNodeName,
  isAllowlistedEmptyNodeName,
  isParsedBrElement,
  isParsedCdataSection,
  isParsedDivTextElement,
  isParsedHeadingElement,
  isParsedHtmlElement,
  isParsedLinkElement,
  isParsedListElement,
  isParsedParagraphElement,
  isParsedSpanElement,
  isParsedTableElement,
  isParsedTextNode,
  isSafeParsedHtmlElement,
  nodeChildTreeContainsNodeWithNames,
  uniqueIdentifierForNode,
} from '@ctw/shared/utils/cda/helpers';

interface NodeRendererProps {
  id: string;
  node: CdaParsedHtmlNode;
  textNodeClassName?: string;
}

export const ReactNodeRenderer = memo(({ id, node, textNodeClassName }: NodeRendererProps) => {
  if (isParsedBrElement(node)) {
    const brIdentifier = uniqueIdentifierForNode({
      node: node,
      context: id,
      purpose: 'br',
    });
    return <br id={brIdentifier} data-testid={DOCUMENT_VIEWER_BR_TEST_ID} />;
  }

  if (elementNamesIntentionallyOmittedFromRendering.includes(node.nodeName as never)) {
    switch (node.nodeName) {
      case 'renderMultiMedia':
        return (
          <Alert type="caution" className={tw`my-3`}>
            This portion of the document contains multimedia XML which cannot be displayed. Please
            download the full XML file to review.
          </Alert>
        );
      default:
        return (
          <Alert type="caution" className={tw`my-3`}>
            This portion of the document contains content which cannot be displayed. Please download
            the full XML file to review.
          </Alert>
        );
    }
  }

  const isAllowlistedEmptyElement =
    isParsedHtmlElement(node) && isAllowlistedEmptyNodeName(node.nodeName);
  if (node.isEffectivelyEmpty && !isAllowlistedEmptyElement) {
    // Ignore nodes that are effectively empty. Note that in some cases these are omitted entirely
    // by the CDA parser for payload optimization, but this explicit guard prevents us from rendering
    // them if they end up here. Allowlisted elements (e.g. `img`) are meaningful despite having no
    // text content, so `isEffectivelyEmpty` alone can't be the whole story for them.
    return <></>;
  }

  if (isParsedTextNode(node)) {
    return (
      <span
        data-testid={DOCUMENT_VIEWER_TEXT_TEST_ID}
        data-search-index={node.searchIndexId ?? undefined}
        className={twx('inline', textNodeClassName)}
      >
        {node.textContent}
      </span>
    );
  }

  if (isParsedCdataSection(node)) {
    const cdataIdentifier = uniqueIdentifierForNode({
      node: node,
      context: id,
      purpose: 'cdata',
    });

    return (
      <span
        data-testid={DOCUMENT_VIEWER_CDATA_TEST_ID}
        data-search-index={node.searchIndexId ?? undefined}
      >
        <ReactHtmlRenderer
          key={cdataIdentifier}
          id={cdataIdentifier}
          parsedHtmlNodes={node.childNodes}
        />
      </span>
    );
  }

  if (isParsedHtmlElement(node)) {
    const linkIdentifier = uniqueIdentifierForNode({
      node: node,
      context: id,
      purpose: 'link',
    });

    if (isParsedLinkElement(node)) {
      return <ReactLinkRenderer key={linkIdentifier} id={linkIdentifier} link={node} />;
    }

    /*
     * footnote: https://build.fhir.org/ig/HL7/CDA-core-sd/narrative.html#footnote-and-footnoteref
     * caption: https://build.fhir.org/ig/HL7/CDA-core-sd/narrative.html#caption
     * content: https://build.fhir.org/ig/HL7/CDA-core-sd/narrative.html#content
     */
    if (['content', 'caption', 'footnote'].includes(node.nodeName)) {
      const narrativeContentIdentifier = uniqueIdentifierForNode({
        node: node,
        context: id,
        purpose: 'narrative-content',
      });

      const childrenIsSoloTextElement =
        node.childNodes.length === 1 && isParsedTextNode(node.childNodes[0]);

      return (
        <span data-search-index={node.searchIndexId ?? undefined}>
          {childrenIsSoloTextElement ? (
            <ReactNodeRenderer
              key={narrativeContentIdentifier}
              id={narrativeContentIdentifier}
              node={node.childNodes[0]}
              textNodeClassName={twx(
                {
                  'cda-content': node.nodeName === 'content',
                  block: ['caption', 'footnote'].includes(node.nodeName),
                  'pt-3 text-content-subtle text-sm italic tracking-wide':
                    node.nodeName === 'caption',
                  'px-5 py-2 text-content-subtle text-sm tracking-wide':
                    node.nodeName === 'footnote',
                },
                node.styles.className,
              )}
            />
          ) : (
            node.childNodes.map((child, index) => {
              const narrativeContentChildIdentifier = uniqueIdentifierForNode({
                node: child,
                context: narrativeContentIdentifier,
                purpose: 'narrative-content-child',
                index,
              });

              return (
                <ReactNodeRenderer
                  id={narrativeContentChildIdentifier}
                  key={narrativeContentChildIdentifier}
                  node={child}
                />
              );
            })
          )}
        </span>
      );
    }

    if (isParsedListElement(node)) {
      const listIdentifier = uniqueIdentifierForNode({
        node: node,
        context: id,
        purpose: 'nonsemantic-list',
      });

      // The presence of certain elements inside a list element indicate that this is not a HTML-semantic list,
      // but rather a set of items that should be treated as ordered elements. Skip the list item
      // itself and render its children as independent nodes.
      if (nodeChildTreeContainsNodeWithNames(node, ['table', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'])) {
        const items = filterChildNodesByNodeName(node, ['item', 'li'], false);

        return (
          <>
            {items.map((item, index) => {
              const listItemIdentifier = uniqueIdentifierForNode({
                node: item,
                context: listIdentifier,
                purpose: 'nonsemantic-list-item',
                index,
              });

              const childrenIsSoloTextElement =
                item.childNodes.length === 1 && isParsedTextNode(item.childNodes[0]);

              return (
                <div key={listItemIdentifier} data-search-index={item.searchIndexId ?? undefined}>
                  {childrenIsSoloTextElement ? (
                    <ReactNodeRenderer
                      key={listItemIdentifier}
                      id={listItemIdentifier}
                      node={item.childNodes[0]}
                      textNodeClassName={twx(item.styles.className, 'block')}
                    />
                  ) : (
                    item.childNodes.map((child, childIndex) => {
                      const listItemChildIdentifier = uniqueIdentifierForNode({
                        node: child,
                        context: listItemIdentifier,
                        purpose: 'nonsemantic-list-item-child',
                        index: childIndex,
                      });

                      return (
                        <ReactNodeRenderer
                          key={listItemChildIdentifier}
                          id={listItemChildIdentifier}
                          node={child}
                        />
                      );
                    })
                  )}
                </div>
              );
            })}
          </>
        );
      }

      return <ReactListRenderer key={listIdentifier} id={listIdentifier} list={node} />;
    }

    if (isParsedHeadingElement(node)) {
      const headingIdentifier = uniqueIdentifierForNode({
        node: node,
        context: id,
        purpose: 'heading',
      });
      return <ReactHeadingRenderer key={headingIdentifier} id={headingIdentifier} heading={node} />;
    }

    // https://build.fhir.org/ig/HL7/CDA-core-sd/narrative.html#table
    if (isParsedTableElement(node)) {
      const tableIdentifier = uniqueIdentifierForNode({
        node: node,
        context: id,
        purpose: 'table',
      });

      return <ReactTableRenderer key={tableIdentifier} id={tableIdentifier} table={node} />;
    }

    // https://build.fhir.org/ig/HL7/CDA-core-sd/narrative.html#sub-and-sup
    if (isParsedParagraphElement(node)) {
      const paragraphIdentifier = uniqueIdentifierForNode({
        node: node,
        context: id,
        purpose: 'paragraph',
      });

      const childrenIsSoloTextElement =
        node.childNodes.length === 1 && isParsedTextNode(node.childNodes[0]);

      return (
        <div data-search-index={node.searchIndexId ?? undefined}>
          {childrenIsSoloTextElement ? (
            <ReactNodeRenderer
              key={paragraphIdentifier}
              id={paragraphIdentifier}
              node={node.childNodes[0]}
              textNodeClassName={twx('block')}
            />
          ) : (
            node.childNodes.map((child, index) => {
              const paragraphChildIdentifier = uniqueIdentifierForNode({
                node: child,
                context: paragraphIdentifier,
                purpose: 'paragraph-child',
                index,
              });

              return (
                <ReactNodeRenderer
                  key={paragraphChildIdentifier}
                  id={paragraphChildIdentifier}
                  node={child}
                />
              );
            })
          )}
        </div>
      );
    }

    if (isParsedDivTextElement(node)) {
      const divTextIdentifier = uniqueIdentifierForNode({
        node: node,
        context: id,
        purpose: 'div-text',
      });

      const childrenIsSoloTextElement =
        node.childNodes.length === 1 && isParsedTextNode(node.childNodes[0]);

      return (
        <div data-search-index={node.searchIndexId ?? undefined}>
          {childrenIsSoloTextElement ? (
            <ReactNodeRenderer
              key={divTextIdentifier}
              id={divTextIdentifier}
              node={node.childNodes[0]}
            />
          ) : (
            node.childNodes.map((child, index) => {
              const divTextChildIdentifier = uniqueIdentifierForNode({
                node: child,
                context: divTextIdentifier,
                purpose: 'div-text-child',
                index,
              });

              return (
                <ReactNodeRenderer
                  key={divTextChildIdentifier}
                  id={divTextChildIdentifier}
                  node={child}
                />
              );
            })
          )}
        </div>
      );
    }

    if (isParsedSpanElement(node)) {
      const spanTextIdentifier = uniqueIdentifierForNode({
        node: node,
        context: id,
        purpose: 'span-text',
      });

      const childrenIsSoloTextElement =
        node.childNodes.length === 1 && isParsedTextNode(node.childNodes[0]);

      return (
        <div className={tw`contents`} data-search-index={node.searchIndexId ?? undefined}>
          {childrenIsSoloTextElement ? (
            <ReactNodeRenderer
              key={spanTextIdentifier}
              id={spanTextIdentifier}
              node={node.childNodes[0]}
            />
          ) : (
            node.childNodes.map((child, index) => {
              const spanTextChildIdentifier = uniqueIdentifierForNode({
                node: child,
                context: spanTextIdentifier,
                purpose: 'span-text-child',
                index,
              });

              return (
                <ReactNodeRenderer
                  key={spanTextChildIdentifier}
                  id={spanTextChildIdentifier}
                  node={child}
                />
              );
            })
          )}
        </div>
      );
    }

    /*
     * CDA narratives can technically contain any HTML/XML tag. Any tag we do not explicitly handle
     * above, we render as-is below if it exists in our safelist.
     *
     * This includes HTML tags we don't explicitly handle, as well as the following from the CDA spec:
     *  - `sup`/`sub`: https://build.fhir.org/ig/HL7/CDA-core-sd/narrative.html#sub-and-sup
     */
    if (isSafeParsedHtmlElement(node)) {
      const safeTagIdentifier = uniqueIdentifierForNode({
        node: node,
        context: id,
        purpose: 'safe-tag',
      });

      const SafeTag = node.nodeName as keyof IntrinsicElements;
      // A void element (e.g. `img`, `hr`) never has children — React throws if it receives a
      // `children` prop at all, even an empty array, so this tag must be self-closing whenever
      // there are no child nodes to render.
      if (node.childNodes.length === 0) {
        return (
          <SafeTag
            id={safeTagIdentifier}
            key={safeTagIdentifier}
            data-search-index={node.searchIndexId ?? undefined}
            {...node.attributes}
          />
        );
      }

      return (
        <SafeTag
          id={safeTagIdentifier}
          key={safeTagIdentifier}
          data-search-index={node.searchIndexId ?? undefined}
          {...node.attributes}
        >
          {node.childNodes.map((child, index) => {
            const childIdentifier = uniqueIdentifierForNode({
              node: child,
              context: safeTagIdentifier,
              purpose: 'safe-tag-child',
              index,
            });

            return <ReactNodeRenderer key={childIdentifier} id={childIdentifier} node={child} />;
          })}
        </SafeTag>
      );
    }
  }

  // Any node that is not Text, CDATA, or an HTML Element in our safelist should not be rendered.
  return <></>;
});
